/**
 * Every machine code this pipeline can emit, translated for a person who does not read code.
 *
 * A code like `E006_EVIDENCE_MISMATCH` tells an engineer everything and a reader nothing. This
 * catalogue is the contract that no raw code reaches the screen alone: each one carries what it
 * means, why the pipeline decided it, and what would change it — the same three-part anatomy every
 * absence state uses (Art Direction §13).
 *
 * Sources: `normalize_prenota.py` header, `transform_prenota.py` failure paths,
 * `normalized_claim.py` warning/error enums, `verification-enrichment-v1.1.md` §19 code tables,
 * and `x_social_review/build_candidate.py` eligibility.
 */

export type DiagnosticSeverity = 'blocks' | 'warns' | 'info';

export type Diagnostic = {
  /** Plain-language name. No code, no snake_case, no English. */
  title: string;
  /** What actually happened, in the editor's terms. */
  what: string;
  /** What would change it, and who can do it. */
  fix: string;
  severity: DiagnosticSeverity;
};

const CATALOGUE: Record<string, Diagnostic> = {
  // ── Prenota normalization — blocking (normalize_prenota.py) ───────────────
  E001_CORRUPT_KEY_CONFLICT: {
    title: 'Datos contradictorios en un claim',
    what: 'El mismo claim llegó con dos versiones distintas de su modalidad (si se afirma, se niega o se atribuye) y no coinciden. El sistema no puede elegir por su cuenta cuál es la correcta.',
    fix: 'Es una falla de extracción. Vuelve a correr la prenota de esta historia; si se repite, repórtalo a desarrollo con el story_id.',
    severity: 'blocks',
  },
  E002_NO_CLAIMS: {
    title: 'La historia se quedó sin afirmaciones',
    what: 'Después de limpiar los datos no quedó ninguna afirmación utilizable. Una nota sin claims no tiene nada que sustentar.',
    fix: 'Normalmente significa que el bloque hablaba de un tema sin hacer afirmaciones concretas. No hay nada que corregir: esta historia no da para nota.',
    severity: 'blocks',
  },
  E002_STATUS_NOT_READY: {
    title: 'La prenota todavía no está lista',
    what: 'Se intentó redactar sobre una prenota que aún no pasó por normalización, o que quedó bloqueada antes.',
    fix: 'Revisa los otros códigos de esta historia: resolver el bloqueo de origen habilita la redacción.',
    severity: 'blocks',
  },
  E003_CLAIM_NO_EVIDENCE: {
    title: 'Un claim sin bloque de origen',
    what: 'Una afirmación no quedó ligada a ningún fragmento del guion, así que no hay forma de comprobar dónde se dijo.',
    fix: 'Es una falla de extracción. Vuelve a correr la prenota; el claim debe apuntar al bloque donde apareció.',
    severity: 'blocks',
  },
  E003_NO_PUBLISHABLE_CLAIM: {
    title: 'Ningún claim es publicable',
    what: 'Todas las afirmaciones de la historia quedaron marcadas como no publicables — normalmente porque cada una excede lo que su cita respalda.',
    fix: 'Revisa los claims de la historia en la pestaña Claims. Si la conferencia sí dijo lo que hace falta, es un problema de extracción; si no, la historia no procede.',
    severity: 'blocks',
  },
  E003_INSUFFICIENT_EVIDENCE_FOR_FORMAT: {
    title: 'No alcanza para el formato pedido',
    what: 'Hay evidencia, pero no la suficiente para el formato solicitado. Un artículo largo necesita más claims sustentados que una nota breve.',
    fix: 'Pide el mismo texto en un formato más corto, o espera a que la verificación agregue hechos externos.',
    severity: 'blocks',
  },
  E004_CLAIM_NO_EXCERPT: {
    title: 'Un claim sin la cita textual',
    what: 'La afirmación existe pero se perdió el fragmento literal del guion que la respalda. Sin la cita no se puede comprobar.',
    fix: 'Es una falla de extracción. Vuelve a correr la prenota de esta historia.',
    severity: 'blocks',
  },
  E006_EVIDENCE_MISMATCH: {
    title: 'El claim no coincide con lo que se dijo',
    what: 'La afirmación que el sistema escribió no comparte términos con la cita del guion que supuestamente la respalda. Dicho de otro modo: dice algo que esa cita no dice.',
    fix: 'Es el bloqueo más importante y es correcto que exista: publicar esto sería atribuirle a alguien algo que no dijo. Contrasta el claim con su cita en la pestaña Claims. Si la cita está mal recortada, vuelve a correr la prenota; si el claim está inventado, la historia no procede.',
    severity: 'blocks',
  },
  E006_FIGURE_BEYOND_EXCERPT: {
    title: 'Una cifra que la cita no menciona',
    what: 'El claim incluye un número que su propia cita nunca dice. Las cifras son lo primero que un lector verifica, así que ese claim pierde publicabilidad — el resto de la historia sigue en pie.',
    fix: 'Confirma la cifra contra el guion. Si se dijo en otro momento de la conferencia, hay que ligar el claim a ese bloque; si no se dijo, la cifra sale de la nota.',
    severity: 'blocks',
  },

  // ── Prenota normalization — warnings ──────────────────────────────────────
  W001_CORRUPT_KEY_MIGRATED: {
    title: 'Se corrigió un campo dañado',
    what: 'Un claim traía un campo con el nombre mal escrito. Todas las versiones decían lo mismo, así que el sistema lo arregló solo.',
    fix: 'No requiere acción.',
    severity: 'info',
  },
  W002_INSTITUTION_NORMALIZED: {
    title: 'Se corrigió el nombre de una institución',
    what: 'La transcripción escribió mal el nombre de una dependencia y el sistema lo normalizó al nombre oficial.',
    fix: 'Verifica que la institución sea la correcta antes de publicar.',
    severity: 'info',
  },
  W003_SINGLE_CHUNK: {
    title: 'Toda la evidencia viene de un solo bloque',
    what: 'La historia se sostiene en un único tramo de la conferencia. Alcanza para una nota breve, es débil para un artículo largo.',
    fix: 'Considera un formato más corto, o usa la verificación para traer contexto externo.',
    severity: 'warns',
  },
  W004_LOW_CLAIM_COUNT: {
    title: 'Pocas afirmaciones materiales',
    what: 'La historia tiene menos de dos afirmaciones con peso. Es publicable, pero da para poco texto.',
    fix: 'Formato corto, o esperar a que la verificación aporte hechos externos.',
    severity: 'warns',
  },
  W005_DERIVED_TEXT_SUSPECT: {
    title: 'El titular menciona una cifra que ningún claim respalda',
    what: 'El encabezado o el resumen incluyen un número que no aparece en ninguna afirmación verificable de la historia.',
    fix: 'Contrasta la cifra del titular contra los claims antes de publicar. Si nadie la dijo, hay que quitarla.',
    severity: 'warns',
  },
  W006_FIGURE_BEYOND_EXCERPT: {
    title: 'Un claim perdió publicabilidad por una cifra',
    what: 'Una afirmación quedó fuera porque incluía un número que su cita no menciona. La historia conserva el resto.',
    fix: 'Revisa ese claim en particular; el resto de la nota no está afectado.',
    severity: 'warns',
  },

  // ── Claim normalization — warnings (normalized_claim.py) ──────────────────
  W_SPEAKER_UNKNOWN: {
    title: 'No se identificó quién lo dijo',
    what: 'El sistema no pudo determinar el hablante de esta afirmación.',
    fix: 'Confirma el hablante en el guion antes de atribuir la frase en la nota.',
    severity: 'warns',
  },
  W_REPORTER_PRESUPPOSITION: {
    title: 'La afirmación viene de la pregunta de un reportero',
    what: 'Lo afirmado estaba dentro de la pregunta, no en la respuesta. Que se pregunte algo no lo convierte en un hecho declarado por el gobierno.',
    fix: 'No lo atribuyas a la persona que responde. Si lo usas, deja claro que salió de la pregunta.',
    severity: 'warns',
  },
  W_LOW_CONTEXT: {
    title: 'Poco contexto alrededor',
    what: 'El claim se extrajo de un tramo con poco contexto, así que puede leerse distinto fuera de su momento.',
    fix: 'Lee el bloque completo en el guion antes de usarlo.',
    severity: 'warns',
  },
  W_LOW_CONFIDENCE: {
    title: 'El modelo tiene poca confianza',
    what: 'La normalización de este claim quedó con confianza baja: el texto canónico puede no reflejar bien lo dicho.',
    fix: 'Compara la columna izquierda y derecha en la pestaña Claims antes de usarlo.',
    severity: 'warns',
  },
  W_DUPLICATE_CANDIDATE: {
    title: 'Posible repetición',
    what: 'Este claim se parece mucho a otro de la misma transmisión. Puede ser la misma afirmación dicha dos veces.',
    fix: 'Si vas a citarla, usa una sola de las dos apariciones.',
    severity: 'info',
  },

  // ── Claim normalization — errors ──────────────────────────────────────────
  E_SCHEMA_INVALID: {
    title: 'El claim llegó con la estructura rota',
    what: 'Faltaba un campo obligatorio o tenía un valor fuera de los permitidos.',
    fix: 'Falla técnica. Vuelve a correr la normalización de la transmisión.',
    severity: 'blocks',
  },
  E_EXCERPT_NOT_FOUND: {
    title: 'La cita no existe en el guion',
    what: 'El fragmento que el sistema citó no aparece literalmente en la transcripción. Una cita que nadie dijo es un error grave, no una imprecisión.',
    fix: 'Falla técnica y bloqueante por diseño. Vuelve a correr; si persiste, repórtalo con el chunk_id.',
    severity: 'blocks',
  },
  E_EMPTY_PROPOSITION: {
    title: 'El claim quedó vacío',
    what: 'Después de normalizar no quedó una afirmación con contenido.',
    fix: 'No requiere acción: ese fragmento no contenía una afirmación.',
    severity: 'blocks',
  },
  E_SEMANTIC_MIX: {
    title: 'Dos temas mezclados en un claim',
    what: 'La afirmación junta hechos o actores que no pertenecen a la misma historia.',
    fix: 'La historia debería dividirse en dos. Vuelve a correr la extracción de prenotas.',
    severity: 'blocks',
  },
  E_ATTRIBUTION_LOST: {
    title: 'Se perdió a quién se le atribuía',
    what: 'El claim citaba a un tercero y esa atribución desapareció al normalizar. Publicarlo así convertiría una cita en una afirmación propia.',
    fix: 'Falla técnica. Vuelve a correr la normalización.',
    severity: 'blocks',
  },
  E_MODALITY_STRENGTHENED: {
    title: 'El sistema afirmó más de lo que se dijo',
    what: 'Lo que se dijo como posibilidad o promesa quedó escrito como un hecho.',
    fix: 'Falla técnica y bloqueante por diseño. Vuelve a correr la normalización.',
    severity: 'blocks',
  },

  // ── Verification — input gates (VE0xx) ────────────────────────────────────
  VE001_SCHEMA_INVALID: {
    title: 'La historia llegó incompleta a verificación',
    what: 'Faltaba un campo obligatorio, así que no se gastó presupuesto de investigación en ella.',
    fix: 'Falla técnica. Corrige la prenota y vuelve a verificar.',
    severity: 'blocks',
  },
  VE002_NOT_ELIGIBLE: {
    title: 'La historia no era elegible para verificar',
    what: 'La prenota venía bloqueada desde antes, así que la verificación no arrancó.',
    fix: 'Resuelve primero el bloqueo de la prenota; la verificación se destraba sola.',
    severity: 'blocks',
  },
  VE003_NOT_RELEVANT: {
    title: 'La historia no se seleccionó',
    what: 'No alcanzó el umbral de relevancia, así que se cerró sin costo externo.',
    fix: 'Si crees que sí es relevante, dispara la transformación a mano desde el API.',
    severity: 'info',
  },
  VE004_NO_RESEARCHABLE_CLAIM: {
    title: 'Nada que se pueda verificar afuera',
    what: 'Ninguna afirmación de la historia es comprobable contra fuentes externas — típicamente son opiniones o anuncios sin dato duro.',
    fix: 'No requiere acción. La nota se escribe solo con lo dicho en la conferencia y será más corta.',
    severity: 'warns',
  },
  VE005_EVIDENCE_MISMATCH: {
    title: 'El claim excede su cita',
    what: 'Igual que E006: la afirmación dice más de lo que su cita respalda.',
    fix: 'Corrige la normalización de ese claim antes de verificar.',
    severity: 'blocks',
  },
  VE006_STORY_MIX: {
    title: 'La historia mezcla eventos distintos',
    what: 'Los claims agrupados pertenecen a hechos o actores incompatibles.',
    fix: 'La historia debe dividirse en dos antes de verificarse.',
    severity: 'blocks',
  },
  VE007_NO_STORY_KEY: {
    title: 'La historia no tiene identificador estable',
    what: 'Sin una llave determinista no se puede reusar ni cachear la verificación.',
    fix: 'Falla técnica. Repórtalo a desarrollo.',
    severity: 'blocks',
  },

  // ── Verification — findings (VE1xx) ───────────────────────────────────────
  VE101_SOURCE_NOT_ALLOWED: {
    title: 'Fuente fuera de las permitidas',
    what: 'Una fuente encontrada no pertenece a las clases autorizadas ni al registro aprobado, así que no se usó.',
    fix: 'No requiere acción. Si la fuente debería aceptarse, hay que agregarla al registro.',
    severity: 'warns',
  },
  VE102_EVIDENCE_NOT_FOUND: {
    title: 'La cita no se localizó en la fuente',
    what: 'El fragmento citado no se encontró literalmente en el documento del que salió.',
    fix: 'No uses esa cita. Vuelve a correr la verificación de esta historia.',
    severity: 'warns',
  },
  VE103_REFERENCE_BROKEN: {
    title: 'Una referencia interna rota',
    what: 'Un identificador usado en la verificación apunta a algo que no existe.',
    fix: 'Falla técnica. Vuelve a correr la verificación.',
    severity: 'warns',
  },
  VE104_CRITICAL_UNRESOLVED: {
    title: 'Un dato crítico quedó sin resolver',
    what: 'Una afirmación central no se pudo confirmar ni descartar, y tampoco se puede atribuir a alguien.',
    fix: 'No publiques ese dato como hecho. O se atribuye explícitamente al hablante, o sale de la nota.',
    severity: 'blocks',
  },
  VE105_CONTRADICTION_HIDDEN: {
    title: 'Una contradicción no llegó al texto',
    what: 'La verificación encontró un conflicto material entre fuentes y el borrador no lo menciona.',
    fix: 'La nota tiene que reportar la disputa. Es exactamente el tipo de omisión que hace daño al publicar.',
    severity: 'blocks',
  },
  VE106_CITATION_ORPHAN: {
    title: 'Una cita sin destino',
    what: 'El texto apunta a una fuente que no corresponde a ningún fragmento real de la nota.',
    fix: 'Revisa las citas del borrador antes de publicar.',
    severity: 'warns',
  },
  VE107_UNSUPPORTED_EXTERNAL_FACT: {
    title: 'Un hecho externo sin respaldo suficiente',
    what: 'La verificación trajo un dato de afuera que no alcanzó el mínimo de fuentes independientes.',
    fix: 'Ese dato no debe publicarse como hecho establecido.',
    severity: 'blocks',
  },
  VE108_MODEL_AS_SOURCE: {
    title: 'Una fuente que nunca se descargó',
    what: 'Una fuente no tiene huella de contenido propia: el modelo la mencionó pero el sistema nunca la bajó. Un modelo no es una fuente.',
    fix: 'Bloqueante por diseño. No cites esa fuente.',
    severity: 'blocks',
  },
  VE108_INSUFFICIENT_FOR_FORMAT: {
    title: 'No hay material para el formato pedido',
    what: 'La verificación no aportó lo suficiente para sostener el largo solicitado.',
    fix: 'Pide un formato más corto.',
    severity: 'blocks',
  },

  // ── Verification — warnings (VWxxx) ───────────────────────────────────────
  VW001_LOW_NORMALIZATION_CONFIDENCE: {
    title: 'Normalización con poca confianza',
    what: 'Los claims que entraron a verificar tienen confianza baja.',
    fix: 'Contrasta los claims contra el guion antes de confiar en los veredictos.',
    severity: 'warns',
  },
  VW002_PROFILE_MISSING: {
    title: 'Sin perfil editorial',
    what: 'No se resolvió un perfil para esta historia; se usaron los valores por omisión.',
    fix: 'No requiere acción inmediata.',
    severity: 'info',
  },
  VW003_LOW_CONTEXT_CAPACITY: {
    title: 'Poco contexto disponible',
    what: 'La verificación halló poco material de contexto, así que la nota tendrá menos profundidad.',
    fix: 'Considera un formato más corto.',
    severity: 'warns',
  },
  VW004_SINGLE_CHUNK: {
    title: 'Evidencia de un solo bloque',
    what: 'Toda la evidencia interna viene del mismo tramo de la conferencia.',
    fix: 'Formato corto, o buscar más contexto externo.',
    severity: 'warns',
  },
  VW101_SINGLE_PROVENANCE: {
    title: 'Todas las fuentes vienen del mismo origen',
    what: 'Las fuentes que confirman el dato pertenecen al mismo grupo — por ejemplo, varias notas que repiten un mismo boletín. Eso no es confirmación independiente.',
    fix: 'Trata el dato como una sola fuente. Si es central, busca confirmación de otro origen antes de publicar.',
    severity: 'warns',
  },
  VW102_STALE_SOURCE: {
    title: 'Fuente posiblemente desactualizada',
    what: 'La fuente es más vieja de lo que este tipo de dato tolera; pudo haber cambiado desde entonces.',
    fix: 'Verifica si hay una cifra más reciente antes de publicar.',
    severity: 'warns',
  },
  VW103_SHORT_NOTE_CAPACITY: {
    title: 'Solo alcanza para nota corta',
    what: 'El material verificado sostiene una nota breve, no un artículo.',
    fix: 'Pide el formato corto.',
    severity: 'warns',
  },
  VW104_PAYWALLED_EVIDENCE: {
    title: 'Evidencia detrás de un muro de pago',
    what: 'Una fuente relevante no se pudo leer completa.',
    fix: 'Si el dato es central, ábrelo manualmente con la suscripción correspondiente.',
    severity: 'warns',
  },
  VW105_PENDING_DOMAIN: {
    title: 'Dominio pendiente de aprobación',
    what: 'Se encontró una fuente en un dominio que aún no está en el registro aprobado.',
    fix: 'Si la fuente es confiable, pide que se agregue al registro.',
    severity: 'info',
  },
  VW106_RESEARCH_DISABLED: {
    title: 'La investigación externa está apagada',
    what: 'No hay proveedor de búsqueda configurado, así que la nota se escribe solo con lo dicho en la conferencia.',
    fix: 'Es configuración del sistema, no un problema de esta historia. Configura RESEARCH_PROVIDER para habilitarla.',
    severity: 'info',
  },

  // ── Generation failures (transform_prenota.py) ────────────────────────────
  angle_planner_exception: {
    title: 'Falló al elegir el ángulo',
    what: 'El modelo que decide el enfoque de la nota se cayó antes de proponer uno.',
    fix: 'Es un error transitorio del proveedor. Vuelve a pedir la transformación.',
    severity: 'blocks',
  },
  drafter_exception: {
    title: 'Falló la redacción',
    what: 'El modelo que escribe el texto se cayó a la mitad.',
    fix: 'Error transitorio del proveedor. Vuelve a pedir la transformación.',
    severity: 'blocks',
  },
  drafter_returned_empty_draft: {
    title: 'La redacción salió vacía',
    what: 'El modelo respondió sin texto.',
    fix: 'Vuelve a pedir la transformación; si se repite con el mismo perfil, repórtalo.',
    severity: 'blocks',
  },
  factual_validator_exception: {
    title: 'Falló el control de fidelidad',
    what: 'El validador que compara la nota contra sus claims no pudo correr. El borrador existe pero nadie lo revisó.',
    fix: 'No publiques sin ese control. Vuelve a pedir la transformación.',
    severity: 'blocks',
  },
  revalidation_failed: {
    title: 'La corrección automática no funcionó',
    what: 'El sistema intentó arreglar los hallazgos y el resultado siguió sin pasar la validación.',
    fix: 'Requiere intervención humana: revisa los hallazgos listados abajo.',
    severity: 'blocks',
  },

  // ── X Social Review ───────────────────────────────────────────────────────
  insufficient_query_specificity: {
    title: 'La historia es demasiado genérica para medirla en X',
    what: 'El titular y los claims no tienen entidades ni datos suficientes para armar una búsqueda que devuelva algo útil. Medir esto daría ruido, no señal.',
    fix: 'No requiere acción. Sin score de X la historia no pasa la compuerta automática de redacción, pero puedes pedir la transformación a mano desde el API.',
    severity: 'info',
  },
  xai_api_error: {
    title: 'Falló la consulta a X',
    what: 'El proveedor de X no respondió correctamente.',
    fix: 'Error del proveedor. Si está marcado como reintentable, vuelve a correr la revisión social.',
    severity: 'warns',
  },
};

/**
 * Never returns null: an unmapped code still reaches an editor, and a bare
 * `E009_SOMETHING_NEW` on screen is exactly the failure this module exists to prevent. The
 * fallback de-jargons what it can and says plainly that the code is unknown.
 */
export function explainCode(rawCode: string): Diagnostic {
  const code = rawCode.trim();
  const known = CATALOGUE[code];
  if (known) return known;

  // Codes arrive with a suffix sometimes: `E002_STATUS_NOT_READY: 'preliminary'`.
  const base = code.split(':')[0]?.trim() ?? code;
  const baseKnown = CATALOGUE[base];
  if (baseKnown) return baseKnown;

  return {
    title: humanizeCode(base),
    what: `El sistema reportó el código ${base}, que este panel todavía no sabe traducir.`,
    fix: 'Repórtalo a desarrollo junto con el identificador de la historia para que se agregue al catálogo.',
    severity: inferSeverity(base),
  };
}

export function isKnownCode(rawCode: string): boolean {
  const code = rawCode.trim();
  return CATALOGUE[code] !== undefined || CATALOGUE[code.split(':')[0]?.trim() ?? ''] !== undefined;
}

/** `E006_EVIDENCE_MISMATCH` → `Evidence mismatch`. Better than the raw token, worse than a real entry. */
function humanizeCode(code: string): string {
  const withoutPrefix = code.replace(/^[EWV]+[0-9]*_?/, '').replace(/_/g, ' ').trim().toLowerCase();
  if (!withoutPrefix) return code;
  return withoutPrefix.charAt(0).toUpperCase() + withoutPrefix.slice(1);
}

function inferSeverity(code: string): DiagnosticSeverity {
  if (code.startsWith('W')) return 'warns';
  if (code.startsWith('E') || code.startsWith('VE')) return 'blocks';
  return 'info';
}

// ── Statuses, also translated ───────────────────────────────────────────────

export const PRENOTA_STATUS: Record<string, { label: string; hint: string }> = {
  preliminary: { label: 'Preliminar', hint: 'Extraída, aún sin normalizar.' },
  normalized: { label: 'Normalizada', hint: 'Limpia y lista para el siguiente paso.' },
  ready_for_editorial: { label: 'Lista para redacción', hint: 'Pasó todos los controles; puede generar notas.' },
  needs_review: { label: 'Necesita revisión', hint: 'Utilizable, pero con advertencias que conviene leer.' },
  blocked: { label: 'Bloqueada', hint: 'No puede generar notas hasta resolver los códigos de abajo.' },
  invalid: { label: 'Inválida', hint: 'La extracción citó algo que no está en el guion; se descartó.' },
};

export const VERIFICATION_STATUS: Record<string, { label: string; hint: string }> = {
  verified: { label: 'Verificada', hint: 'Se contrastó contra fuentes externas.' },
  contradiction_lead: {
    label: 'Contradicción encontrada',
    hint: 'Las fuentes externas contradicen algo de lo dicho. Suele ser la nota más valiosa del día.',
  },
  blocked_critical_claim: {
    label: 'Detenida por un dato crítico',
    hint: 'Una afirmación central no se pudo resolver.',
  },
  blocked_no_sources: { label: 'Sin fuentes', hint: 'No se encontró ninguna fuente admisible para contrastar.' },
  blocked_input: { label: 'No se pudo iniciar', hint: 'La historia no cumplía los requisitos para verificarse.' },
  research_failed: { label: 'Falló la investigación', hint: 'El proveedor de búsqueda no respondió.' },
};

export const OUTPUT_STATUS: Record<string, { label: string; hint: string }> = {
  pending_human_review: {
    label: 'Lista para revisión',
    hint: 'Pasó los controles automáticos. Falta que una persona la apruebe.',
  },
  validation_failed: {
    label: 'No pasó validación',
    hint: 'El texto no resistió el control de fidelidad o de estilo. Léela con cuidado.',
  },
  blocked_eligibility: {
    label: 'No elegible',
    hint: 'La historia de origen está bloqueada, así que no se redactó.',
  },
  generation_failed: { label: 'Falló al generar', hint: 'Error del proveedor durante la redacción. Es reintentable.' },
};
